import { MdExpandMore, MdExpandLess, MdAdd, MdWarning, MdLock, MdPerson } from 'react-icons/md';
import { adToBs } from '../../utils/calendar';
import useImpersonation from '../../hooks/useImpersonation';

const getStudentName = (student) => student?.name || student?.email || 'Unknown student';
const getStudentId   = (student) => student?.txId || '';

const CATEGORY_LABELS = {
  technique: 'Technique',
  attendance: 'Attendance',
  belt_readiness: 'Belt Readiness',
  behavior: 'Behavior',
  injury: 'Injury',
  safety: 'Safety',
  general: 'General',
  other: 'Other',
};

// Type: 3px left accent bar (saturated) + soft tinted chip.
const TYPE_META = {
  progress_note:   { label: 'Progress note', accent: 'bg-green-600', chip: 'bg-green-500/15 text-green-600' },
  incident_report: { label: 'Incident',      accent: 'bg-red-600',   chip: 'bg-red-500/15 text-red-600' },
};

// Severity: soft tints, readable in both themes.
const SEVERITY_META = {
  low:    { label: 'Low',    chip: 'bg-green-500/15 text-green-600' },
  medium: { label: 'Medium', chip: 'bg-amber-500/15 text-amber-600' },
  high:   { label: 'High',   chip: 'bg-red-500/15 text-red-600' },
};

// AD short, e.g. "3 May 2026"
const fmtAd = (d) => d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
// BS primary · AD secondary, e.g. "Baisakh 20, 2083 · 3 May 2026" (reuses adToBs).
const fmtBsAd = (raw) => {
  if (!raw) return '';
  const d = new Date(raw);
  try {
    const bs = adToBs(d);
    return `${bs.monthName} ${bs.day}, ${bs.year} · ${fmtAd(d)}`;
  } catch {
    return fmtAd(d);
  }
};

function RecordCard({ record, isExpanded, onToggle, onAddFollowUp, canAddFollowUp }) {
  const { isReadOnly } = useImpersonation();
  const isIncident = record.recordType === 'incident_report';
  const meta = TYPE_META[record.recordType] || TYPE_META.progress_note;
  const sev  = isIncident ? SEVERITY_META[record.severity] : null;
  const memberId = getStudentId(record.studentId);
  const followUps = record.followUps?.length || 0;

  return (
    <div className="relative overflow-hidden bg-card border border-divider rounded-2xl">
      {/* Type accent bar */}
      <span className={`absolute left-0 top-0 bottom-0 w-[3px] ${meta.accent}`} />

      <div className="p-4">
        {/* Header (click to expand) */}
        <div className="flex items-start justify-between gap-3 cursor-pointer" onClick={onToggle}>
          <div className="flex-1 min-w-0">
            {/* Chips */}
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full inline-flex items-center gap-1 ${meta.chip}`}>
                {isIncident && <MdWarning className="text-sm" />} {meta.label}
              </span>
              <span className="text-xs font-medium bg-nav-hover text-muted px-2 py-0.5 rounded-full">
                {CATEGORY_LABELS[record.category] || record.category}
              </span>
              {isIncident && sev && (
                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${sev.chip}`}>
                  {sev.label} severity
                </span>
              )}
              {record.isLocked && (
                <span className="text-xs font-medium bg-nav-hover text-subtle px-2 py-0.5 rounded-full inline-flex items-center gap-1">
                  <MdLock className="text-sm" /> Locked
                </span>
              )}
              {followUps > 0 && (
                <span className="text-xs font-medium bg-blue-500/15 text-blue-600 px-2 py-0.5 rounded-full">
                  {followUps} follow-up{followUps !== 1 ? 's' : ''}
                </span>
              )}
            </div>

            {/* Title */}
            <h3 className="font-medium text-secondary text-base leading-snug mt-2">{record.title}</h3>

            {/* Student */}
            <div className="flex items-center gap-1.5 text-sm text-secondary mt-1 min-w-0">
              <span className="truncate">{getStudentName(record.studentId)}</span>
              {memberId && <span className="text-xs text-muted font-mono flex-shrink-0">· {memberId}</span>}
            </div>

            {/* Author · BS·AD date */}
            <div className="flex items-center gap-1.5 text-xs text-subtle mt-1.5">
              <MdPerson className="text-sm flex-shrink-0" />
              <span className="truncate">by {record.createdBy?.name || 'Unknown'} · {fmtBsAd(record.date)}</span>
            </div>
          </div>

          <button
            onClick={(e) => { e.stopPropagation(); onToggle(); }}
            className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full text-muted hover:bg-nav-hover transition-colors"
            aria-label={isExpanded ? 'Collapse' : 'Expand'}
          >
            {isExpanded ? <MdExpandLess className="text-lg" /> : <MdExpandMore className="text-lg" />}
          </button>
        </div>

        {/* Expanded content */}
        {isExpanded && (
          <div className="mt-3 pt-3 border-t border-divider space-y-3">
            <div>
              <p className="text-xs font-semibold text-muted uppercase tracking-wide mb-1">Content</p>
              <p className="text-sm text-secondary whitespace-pre-wrap leading-relaxed">{record.content}</p>
            </div>

            {isIncident && (record.actionTaken || record.followUpRequired) && (
              <div className="bg-nav-hover border border-divider rounded-xl p-3 space-y-2">
                {record.actionTaken && (
                  <div>
                    <p className="text-xs font-semibold text-muted uppercase tracking-wide mb-1">Action taken</p>
                    <p className="text-sm text-secondary">{record.actionTaken}</p>
                  </div>
                )}
                {record.followUpRequired && (
                  <p className="flex items-center gap-1 text-xs font-medium text-amber-600">
                    <MdWarning className="text-sm" /> Follow-up required
                  </p>
                )}
              </div>
            )}

            {followUps > 0 && (
              <div>
                <p className="text-xs font-semibold text-muted uppercase tracking-wide mb-2">Follow-ups</p>
                <div className="space-y-2">
                  {record.followUps.map((followUp, idx) => (
                    <div key={idx} className="bg-nav-hover border border-divider rounded-xl p-3">
                      <p className="text-xs text-muted mb-1">
                        {followUp.addedBy?.name || 'Unknown'} · {fmtAd(new Date(followUp.addedAt))}
                      </p>
                      <p className="text-sm text-secondary whitespace-pre-wrap">{followUp.note}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {canAddFollowUp && (
              <button
                onClick={() => onAddFollowUp(record._id)}
                disabled={isReadOnly}
                title={isReadOnly ? 'Disabled: read-only support mode' : undefined}
                className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium bg-card text-secondary border border-divider rounded-full hover:bg-secondary hover:text-card hover:border-secondary transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <MdAdd className="text-base" /> Add follow-up
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default RecordCard;
